import { useState } from 'react';
import { Paperclip } from 'lucide-react';

import {
  MEDBIN_FILE_ACCEPT,
  buildXbin,
  buildXdis91Parent,
  checkMedbinFile,
  medbinLimitMessage,
  xmlDownloadBlob,
  validateXbinXml,
  xmlObjectCodes,
  type MedbinKind,
  type MedbinValidation,
} from '@/lib/medbin';
import { useOpsMedbinPreviews } from '@/hooks/useOpsDashboard';

const inputClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function field(label: string, value: string, onChange: (value: string) => void) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">{label}</span>
      <input className={inputClass} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

export function Medbin() {
  const [consumerName, setConsumerName] = useState('');
  const [kind, setKind] = useState<MedbinKind>('XBIN01');
  const [senderEan, setSenderEan] = useState('5790001987244');
  const [senderName, setSenderName] = useState('Clinio Test Klinik');
  const [receiverEan, setReceiverEan] = useState('5790002520938');
  const [receiverName, setReceiverName] = useState('');
  const [patientCpr, setPatientCpr] = useState('');
  const [patientGiven, setPatientGiven] = useState('');
  const [patientSurname, setPatientSurname] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [xml, setXml] = useState('');
  const [parentXml, setParentXml] = useState('');
  const [validation, setValidation] = useState<MedbinValidation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stored = useOpsMedbinPreviews(0);

  const onBuild = async () => {
    setError(null);
    setValidation(null);
    setParentXml('');
    const file = files[0];
    if (!file) {
      setError('Choose a file.');
      return;
    }
    const extension = file.name.includes('.') ? file.name.split('.').pop() ?? '' : '';
    const bytes = new Uint8Array(await file.arrayBuffer());
    const sentDate = new Date().toISOString().slice(0, 10);
    const sentTime = new Date().toISOString().slice(11, 16);
    const objectIdentifier = crypto.randomUUID();
    const sender = {
      ean: senderEan,
      identifier: senderEan,
      identifierCode: 'lokationsnummer',
      organisationName: senderName,
    };
    const receiver = {
      ean: receiverEan,
      identifier: receiverEan,
      identifierCode: 'lokationsnummer',
      organisationName: receiverName || receiverEan,
    };
    try {
      const codes = xmlObjectCodes(extension);
      const built = buildXbin({
        kind,
        envelopeIdentifier: crypto.randomUUID(),
        sentDate,
        sentTime,
        acknowledgementCode: 'minuspositivkvitt',
        letterIdentifier: crypto.randomUUID(),
        authorisationDate: sentDate,
        authorisationTime: sentTime,
        statusCode: 'nytbrev',
        sender,
        receiver,
        patientCpr,
        patientSurname,
        patientGivenName: patientGiven,
        objectIdentifier,
        objectCode: codes.objectCode,
        objectExtensionCode: codes.objectExtensionCode,
        objectBytes: bytes,
      });
      if (kind === 'XBIN01') {
        setParentXml(
          buildXdis91Parent({
            envelopeIdentifier: crypto.randomUUID(),
            letterIdentifier: crypto.randomUUID(),
            sentDate,
            sentTime,
            authorisationDate: sentDate,
            authorisationTime: sentTime,
            sender,
            receiver,
            patientCpr,
            patientSurname,
            patientGivenName: patientGiven,
            objectIdentifier,
            objectCode: codes.objectCode,
            objectExtensionCode: codes.objectExtensionCode,
            originalObjectSize: bytes.byteLength,
          }),
        );
      }
      setXml(built);
      setValidation(validateXbinXml(built, consumerName));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build the file.');
    }
  };

  const download = (name: string, contents: string) => {
    try {
      const blob = xmlDownloadBlob(contents);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error
          ? `Cannot download ${name}: ${err.message}`
          : `Cannot download ${name}.`,
      );
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">MEDBIN</h1>
      <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
        Attachment file for one Communicate customer. XBIN01 hangs off a correspondence.
        XBIN02 hangs off a referral. This screen checks the file. It does not send it.
      </p>

      <div className="mt-6 grid max-w-3xl gap-4">
        {field('Customer', consumerName, setConsumerName)}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">Attachment type</span>
          <select
            className={inputClass}
            value={kind}
            onChange={(event) => setKind(event.target.value as MedbinKind)}
          >
            <option value="XBIN01">XBIN01 — file on a correspondence</option>
            <option value="XBIN02">XBIN02 — file on a referral</option>
          </select>
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          {field('Sender EAN', senderEan, setSenderEan)}
          {field('Sender name', senderName, setSenderName)}
          {field('Receiver EAN', receiverEan, setReceiverEan)}
          {field('Receiver name', receiverName, setReceiverName)}
          {field('Patient CPR', patientCpr, setPatientCpr)}
          {field('Patient given name', patientGiven, setPatientGiven)}
          {field('Patient surname', patientSurname, setPatientSurname)}
        </div>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">Files</span>
          <input
            type="file"
            multiple
            accept={MEDBIN_FILE_ACCEPT}
            className="text-sm text-gray-700 dark:text-gray-200"
            onChange={(event) => {
              const picked = Array.from(event.target.files ?? []);
              event.target.value = '';
              let next = [...files];
              let refused: string | null = null;
              for (const pickedFile of picked) {
                const limit = checkMedbinFile({
                  fileName: pickedFile.name,
                  byteLength: pickedFile.size,
                  existingFileCount: next.length,
                  existingTotalBytes: next.reduce((sum, item) => sum + item.size, 0),
                });
                if (!limit.ok) {
                  refused = medbinLimitMessage(limit.code);
                  break;
                }
                next.push(pickedFile);
              }
              setFiles(next);
              setError(refused);
            }}
          />
        </label>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          The check runs on the first file. The list enforces 10 files and 100 MB. A pair over 100 MB is refused.
        </p>
        {files.length > 0 && (
          <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-100">
            {files.map((item) => (
              <li key={`${item.name}-${item.size}-${item.lastModified}`} className="flex items-center justify-between gap-3">
                <span>
                  {item.name} ({Math.ceil(item.size / 1024)} KB)
                </span>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-800"
                  onClick={() => setFiles((current) => current.filter((entry) => entry !== item))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <div>
          <button
            type="button"
            onClick={() => void onBuild()}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            <Paperclip className="h-4 w-4" />
            Build and check
          </button>
        </div>
      </div>

      <div className="mt-10 max-w-5xl">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Stored checks</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Files a customer sent through the API. The form above only checks a file in this browser.
        </p>
        {stored.isLoading && <p className="mt-3 text-sm text-gray-600">Loading stored checks.</p>}
        {stored.error && <p className="mt-3 text-sm text-red-600">Could not load stored checks.</p>}
        {stored.data && stored.data.items.length === 0 && (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">No stored checks yet.</p>
        )}
        {stored.data && stored.data.items.length > 0 && (
          <table className="mt-3 w-full text-left text-sm text-gray-800 dark:text-gray-100">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500 dark:border-gray-700">
                <th className="py-2 pr-3 font-medium">Customer</th>
                <th className="py-2 pr-3 font-medium">Type</th>
                <th className="py-2 pr-3 font-medium">File</th>
                <th className="py-2 pr-3 font-medium">Result</th>
              </tr>
            </thead>
            <tbody>
              {stored.data.items.map((item) => (
                <tr key={item.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-3">{item.consumerName}</td>
                  <td className="py-2 pr-3">{item.kind}</td>
                  <td className="py-2 pr-3">{item.fileName}</td>
                  <td className={item.validationOk ? 'text-green-700 dark:text-green-400' : 'text-red-600'}>
                    {item.validationOk ? 'Pass' : 'Fail'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      {validation && (
        <div className="mt-8 max-w-3xl">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {validation.ok ? 'Checks passed' : 'Checks failed'}
          </h2>
          <ul className="mt-3 space-y-2">
            {validation.checks.map((item) => (
              <li key={item.id} className="text-sm text-gray-800 dark:text-gray-100">
                <span className={item.pass ? 'text-green-700 dark:text-green-400' : 'text-red-600'}>
                  {item.pass ? 'Pass' : 'Fail'}
                </span>
                {' — '}
                {item.detail}
              </li>
            ))}
          </ul>
          {xml && (
            <div className="mt-4 flex gap-2">
              {parentXml && (
                <button
                  type="button"
                  onClick={() => download('XDIS91.xml', parentXml)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
                >
                  Download parent
                </button>
              )}
              <button
                type="button"
                onClick={() => download(kind === 'XBIN01' ? 'XBIN01.xml' : 'XBIN02.xml', xml)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-800"
              >
                Download MEDBIN
              </button>
            </div>
          )}
          <pre className="mt-4 max-h-80 overflow-auto rounded-md bg-gray-900 p-4 text-xs text-gray-100">{xml}</pre>
        </div>
      )}
    </div>
  );
}
